import DocumentsService from '@/services/documents.service'

const state = {
  items: [],
  currentItem: null,
  pagination: {
    total: 0,
    perPage: 15,
    currentPage: 1,
    lastPage: 1,
  },
  filters: {},
  loading: false,
  error: null,
}

const getters = {
  items: (state) => state.items || [],
  currentItem: (state) => state.currentItem,
  pagination: (state) => state.pagination,
  loading: (state) => state.loading,
  error: (state) => state.error,
}

const mutations = {
  SET_ITEMS(state, items) {
    state.items = Array.isArray(items) ? items : []
  },
  SET_CURRENT_ITEM(state, item) {
    state.currentItem = item
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total || 0,
      perPage: pagination.per_page || 15,
      currentPage: pagination.current_page || 1,
      lastPage: pagination.last_page || 1,
    }
  },
  SET_FILTERS(state, filters) {
    state.filters = filters
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  RESET_FILTERS(state) {
    state.filters = {}
  },
}

const actions = {
  async fetchAll({ commit, state }, { page = 1, perPage = 15 } = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      // Build params with pagination and filters
      const params = {
        page,
        per_page: perPage,
        '_with_[documentPeople.person]': '',
        '_with_[files]': '',
        ...state.filters,
      }

      // Remove empty filter values (but keep _with_ params)
      Object.keys(params).forEach(key => {
        if (!key.startsWith('_with_') && (params[key] === null || params[key] === '' || params[key] === undefined)) {
          delete params[key]
        }
      })

      const response = await DocumentsService.getAll(params)

      if (response?.success) {
        const data = response.data
        
        // Handle Laravel pagination response
        if (data?.data && Array.isArray(data.data)) {
          commit('SET_ITEMS', data.data)
          commit('SET_PAGINATION', data)
        } else if (Array.isArray(data)) {
          commit('SET_ITEMS', data)
        } else {
          commit('SET_ITEMS', [])
        }
      } else {
        commit('SET_ITEMS', [])
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error)
      commit('SET_ERROR', error.response?.data?.message || 'فشل في جلب الوثائق')
      commit('SET_ITEMS', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchById({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await DocumentsService.getById(id, {
        '_with_[documentPeople.person]': '',
        '_with_[files]': '',
      })

      if (response?.success) {
        commit('SET_CURRENT_ITEM', response.data)
        return { success: true, data: response.data }
      }
      
      return { success: false, message: response?.message || 'فشل في جلب الوثيقة' }
    } catch (error) {
      const message = error.response?.data?.message || 'فشل في جلب الوثيقة'
      commit('SET_ERROR', message)
      return { success: false, message }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async create({ commit, dispatch }, formData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await DocumentsService.create(formData)

      if (response?.success) {
        await dispatch('fetchAll')
        return { success: true, message: response.message || 'تم إضافة الوثيقة بنجاح' }
      }
      
      return { success: false, message: response?.message || 'فشل في إضافة الوثيقة' }
    } catch (error) {
      const message = error.response?.data?.message || 'فشل في إضافة الوثيقة'
      commit('SET_ERROR', message)
      return { success: false, message }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async update({ commit, dispatch }, { id, formData }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await DocumentsService.update(id, formData)

      if (response?.success) {
        await dispatch('fetchAll')
        return { success: true, message: response.message || 'تم تحديث الوثيقة بنجاح' }
      }
      
      return { success: false, message: response?.message || 'فشل في تحديث الوثيقة' }
    } catch (error) {
      const message = error.response?.data?.message || 'فشل في تحديث الوثيقة'
      commit('SET_ERROR', message)
      return { success: false, message }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async delete({ commit, dispatch }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await DocumentsService.delete(id)

      if (response?.success) {
        await dispatch('fetchAll')
        return { success: true, message: response.message || 'تم حذف الوثيقة بنجاح' }
      }
      
      return { success: false, message: response?.message || 'فشل في حذف الوثيقة' }
    } catch (error) {
      const message = error.response?.data?.message || 'فشل في حذف الوثيقة'
      commit('SET_ERROR', message)
      return { success: false, message }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  resetFilters({ commit }) {
    commit('RESET_FILTERS')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
